import { PlayerAPI } from 'bitmovin-player';

/**
 * Bitmovin's `Player.destroy()` removes the instance from its internal registry *synchronously*
 * and then tears down asynchronously (the `Destroy` event is fired from that async phase). Any
 * public API call made after that point — from bitmovin-player-ui timers/handlers, Broadpeak
 * SmartLib, the NPAW adapter, EPG callbacks, delayed timeouts, … — throws
 * `API_NOT_AVAILABLE` (code 1001): "Cannot use the `player.x` API: the player has already been destroyed."
 *
 * This module tracks destroyed players and wraps the public API so that late calls degrade to
 * harmless defaults instead of throwing.
 */

const API_NOT_AVAILABLE_CODE = 1001;

/** Players whose API has been destroyed (either by us or detected through a 1001 error). */
const destroyedPlayers = new WeakSet<object>();
/** Players whose API surface has already been wrapped by `guardPlayerApi`. */
const guardedPlayers = new WeakSet<object>();

export const isApiNotAvailableError = (e: unknown): boolean => {
    const err = e as { name?: string; code?: number } | undefined;
    return err?.name === 'API_NOT_AVAILABLE' || err?.code === API_NOT_AVAILABLE_CODE;
};

/** Record that a player's API is gone. Idempotent. */
export const markPlayerDestroyed = (player?: object | null): void => {
    if (player) destroyedPlayers.add(player);
};

export const isPlayerAlive = (player?: PlayerAPI | null): boolean => {
    if (!player) return false;
    if (destroyedPlayers.has(player)) return false;

    try {
        // For guarded players this never throws; the wrapper marks the player destroyed instead.
        player.getSource();
    } catch (e) {
        if (isApiNotAvailableError(e)) markPlayerDestroyed(player);
        return false;
    }

    return !destroyedPlayers.has(player);
};

// ---------------------------------------------------------------------------------------------
// Safe defaults returned once the player is destroyed
// ---------------------------------------------------------------------------------------------

const PROMISE_METHODS = new Set(['load', 'unload', 'play', 'destroy', 'isDRMSupported', 'getSupportedDRM']);
const ARRAY_METHODS = new Set([
    'getAvailableAudio',
    'getAvailableAudioQualities',
    'getAvailableVideoQualities',
    'getAvailableSegments',
    'getBufferedRanges',
    'getSupportedTech',
]);
const NUMBER_METHODS = new Set([
    'getCurrentTime',
    'getDuration',
    'getTimeShift',
    'getMaxTimeShift',
    'getVolume',
    'getAspectRatio',
    'getDroppedVideoFrames',
    'getTotalStalledTime',
    'getAudioBufferLength',
    'getVideoBufferLength',
]);
const CHAINABLE_METHODS = new Set(['on', 'off', 'addEventListener', 'removeEventListener']);

const defaultReturnFor = (player: object, name: string): unknown => {
    if (PROMISE_METHODS.has(name)) return Promise.resolve(name === 'getSupportedDRM' ? [] : undefined);
    if (CHAINABLE_METHODS.has(name)) return player;
    if (ARRAY_METHODS.has(name)) return [];
    if (NUMBER_METHODS.has(name)) return 0;
    if (name === 'getSeekableRange') return { start: 0, end: 0 };
    if (name === 'getPlaybackSpeed') return 1;
    if (name === 'getConfig') return {};
    if (name === 'getViewMode') return 'inline';
    if (/^(is|has)[A-Z]/.test(name)) return false;
    return undefined;
};

/**
 * Stand-in for API namespaces (`player.subtitles`, `player.buffer`, `player.ads`, …) of a destroyed
 * player: every property is a no-op function, `list()` returns an empty array.
 */
const noopNamespace: object = new Proxy(Object.create(null), {
    get: (_target, prop) => {
        if (typeof prop === 'symbol') return undefined;
        if (prop === 'list') return () => [];
        return () => undefined;
    },
});

const collectPrototypeDescriptors = (player: object): Map<string, PropertyDescriptor> => {
    const descriptors = new Map<string, PropertyDescriptor>();
    let proto = Object.getPrototypeOf(player);

    while (proto && proto !== Object.prototype) {
        for (const name of Object.getOwnPropertyNames(proto)) {
            if (name === 'constructor' || descriptors.has(name)) continue;
            const descriptor = Object.getOwnPropertyDescriptor(proto, name);
            if (descriptor) descriptors.set(name, descriptor);
        }
        proto = Object.getPrototypeOf(proto);
    }

    return descriptors;
};

/**
 * Wraps the whole public API of a Bitmovin player instance so calls made after `destroy()` never
 * throw `API_NOT_AVAILABLE`. While the player is alive every call passes straight through.
 *
 * Must be applied right after `new Player(...)`, before any `UIManager`/adapter is attached:
 * bitmovin-player-ui's `PlayerWrapper` captures property descriptors at construction time, so the
 * wrappers have to already be own properties of the instance for getter namespaces
 * (`player.subtitles`, …) to be intercepted.
 *
 * Idempotent.
 */
export const guardPlayerApi = <T extends PlayerAPI>(player: T): T => {
    if (!player || guardedPlayers.has(player)) return player;
    guardedPlayers.add(player);

    const target = player as unknown as Record<string, unknown>;

    collectPrototypeDescriptors(player).forEach((descriptor, name) => {
        // Accessor namespaces (subtitles, vr, buffer, ads, …)
        if (typeof descriptor.get === 'function') {
            const originalGet = descriptor.get;
            const originalSet = descriptor.set;
            Object.defineProperty(target, name, {
                configurable: true,
                enumerable: descriptor.enumerable,
                get() {
                    // Some accessors (e.g. `exports`, `version`) stay usable after destroy, so always
                    // try the real getter first and only fall back when Bitmovin reports 1001.
                    try {
                        return originalGet.call(player);
                    } catch (e) {
                        if (!isApiNotAvailableError(e)) throw e;
                        markPlayerDestroyed(player);
                        return noopNamespace;
                    }
                },
                ...(originalSet
                    ? {
                          set(value: unknown) {
                              try {
                                  originalSet.call(player, value);
                              } catch (e) {
                                  if (!isApiNotAvailableError(e)) throw e;
                                  markPlayerDestroyed(player);
                              }
                          },
                      }
                    : {}),
            });
            return;
        }

        if (typeof descriptor.value !== 'function') return;

        const original = descriptor.value as (...args: unknown[]) => unknown;

        Object.defineProperty(target, name, {
            configurable: true,
            writable: true,
            enumerable: descriptor.enumerable,
            value: function guardedPlayerMethod(...args: unknown[]) {
                if (name === 'destroy') {
                    // The API is unusable from this point on, regardless of the async teardown.
                    markPlayerDestroyed(player);
                } else if (destroyedPlayers.has(player)) {
                    return defaultReturnFor(player, name);
                }

                try {
                    // Always invoke with the real instance as `this`, even for unbound calls.
                    const result = original.apply(player, args);
                    // Bitmovin Player UI calls `player.play('ui')` with no `.catch()`. When the user
                    // toggles play→pause (or ads/visibility pause while play is in flight), the
                    // browser rejects with AbortError and that surfaces as an unhandled rejection.
                    if (name === 'play' && result != null && typeof (result as Promise<unknown>).then === 'function') {
                        return (result as Promise<unknown>).catch((error: unknown) => {
                            if (isPlayAbortError(error) || isApiNotAvailableError(error)) return;
                            throw error;
                        });
                    }
                    return result;
                } catch (e) {
                    if (!isApiNotAvailableError(e)) throw e;
                    markPlayerDestroyed(player);
                    return defaultReturnFor(player, name);
                }
            },
        });
    });

    return player;
};

const isPlayAbortError = (error: unknown): boolean =>
    (error as DOMException | undefined)?.name === 'AbortError';

/**
 * Starts playback without letting an interrupted `play()` surface as an unhandled
 * rejection. Prefer this over raw `player.play()` in app code; Bitmovin UI clicks are
 * covered by the `play` wrapper in `guardPlayerApi`.
 */
export const safePlayerPlay = (player?: Pick<PlayerAPI, 'play'> | null, issuer?: string) => {
    if (!player) return;

    try {
        player.play(issuer)?.catch((error: unknown) => {
            if (isPlayAbortError(error)) return;
            if (isApiNotAvailableError(error)) return;
            console.warn('Player play() was rejected:', error);
        });
    } catch (e) {
        if (isApiNotAvailableError(e)) return;
        console.warn('Player play() was rejected:', e);
        // the player was destroyed mid-call
    }
};

export const safePlayerOn = (player: PlayerAPI | null, event: any, handler: (...args: any[]) => void) => {
    if (!player || !isPlayerAlive(player)) return;

    try {
        player.on(event, handler);
    } catch (e) {
        if (isApiNotAvailableError(e)) markPlayerDestroyed(player);
    }
};

export const safePlayerOff = (player: PlayerAPI | null, event: any, handler: (...args: any[]) => void) => {
    if (!player) return;

    try {
        player.off(event, handler);
    } catch (e) {
        if (isApiNotAvailableError(e)) markPlayerDestroyed(player);
    }
};
